import { Box, Stack, SxProps, Typography, useTheme } from '@mui/material';
import { MenuItem as MUIMenuItem } from '@mui/material';
import * as React from 'react';

import { CheckIcon } from '@/components/icons/CheckIcon';
import { Loader } from '@/components/progress/Loader/Loader';
import { COLORS } from '@/styles/tokens/colors';

export interface MenuItemProps {
  /** Show the selected state and a checkmark without replacing other icons. */
  selected?: boolean;
  variant?: 'default' | 'destructive';
  icon?: React.ReactNode;
  iconSize?: number;
  iconAfter?: React.ReactNode;
  /**
   * Content rendered immediately to the right of the label text on the
   * same row (e.g. an info tooltip). Unlike `iconAfter`, which sits at
   * the far right of the row, `endAdornment` hugs the label.
   */
  endAdornment?: React.ReactNode;
  label: React.ReactNode;
  subtext?: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  justifyContent?: 'left' | 'right' | 'space-between';
  loading?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  typographyProps?: React.ComponentProps<typeof Typography>;
  subtextTypographyProps?: React.ComponentProps<typeof Typography>;
  muiMenuItemProps?: React.ComponentProps<typeof MUIMenuItem>;
  dataTestId?: string;
  /**
   * Disable the default hover→focus behavior. By default, hovering a
   * MenuItem moves DOM focus onto it so a keyboard handoff continues
   * from where the mouse left off. Pass `true` to opt out — useful when
   * a parent component already manages focus on this row.
   */
  disableFocusOnHover?: boolean;
}

export function MenuItem({
  selected,
  variant = 'default',
  icon,
  iconSize = 16,
  iconAfter,
  endAdornment,
  label,
  subtext,
  onClick,
  justifyContent,
  disabled,
  loading,
  sx,
  typographyProps,
  subtextTypographyProps,
  muiMenuItemProps,
  dataTestId,
  disableFocusOnHover,
}: MenuItemProps) {
  const theme = useTheme();
  const isDestructive = variant === 'destructive';

  // Hover → focus by default, so a keyboard handoff continues from where
  // the mouse left off. Skip when an input has focus so passive mouse
  // movement over the list doesn't steal keystrokes from e.g. a search
  // input above the menu. Consumers can opt out via `disableFocusOnHover`
  // or replace the behavior by passing their own `onMouseEnter`.
  const handleMouseEnter = (e: React.MouseEvent<HTMLLIElement>) => {
    if (
      !disableFocusOnHover &&
      !(document.activeElement instanceof HTMLInputElement)
    ) {
      e.currentTarget.focus();
    }
    muiMenuItemProps?.onMouseEnter?.(e);
  };

  return (
    <MUIMenuItem
      data-testid={dataTestId}
      sx={{
        color: isDestructive ? COLORS.FUNCTIONAL.ERROR[700] : undefined,
        ...(isDestructive && {
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: COLORS.FUNCTIONAL.ERROR[50],
          },
        }),
        ...sx,
        justifyContent: justifyContent ?? 'left',
      }}
      onClick={onClick}
      disabled={disabled || loading}
      {...muiMenuItemProps}
      selected={selected ?? muiMenuItemProps?.selected}
      onMouseEnter={handleMouseEnter}
    >
      <Stack
        direction="row"
        justifyContent={justifyContent}
        alignItems={'center'}
        spacing={1}
        width={'100%'}
        sx={{
          ...(!iconAfter &&
            !selected &&
            !loading && {
              // Reserve the chevron column for leaf rows in a submenu menu.
              '.MuiMenu-list:has([aria-haspopup="menu"]) &::after': {
                content: '""',
                width: 16,
                flexShrink: 0,
                marginLeft: 2,
              },
            }),
          ...((iconAfter || selected || loading) && {
            '& > :last-child': { marginLeft: 2, flexShrink: 0 },
          }),
        }}
      >
        {icon && (
          <Box
            display="flex"
            height={iconSize}
            width={iconSize}
            sx={{
              '& > *, & svg': { height: iconSize, width: iconSize },
            }}
          >
            {icon}
          </Box>
        )}
        <Stack direction="column" spacing={0} flex={1}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="body-xs"
              sx={{
                color: isDestructive
                  ? COLORS.FUNCTIONAL.ERROR[700]
                  : COLORS.TEXT.PRIMARY,
              }}
              {...typographyProps}
            >
              {label}
            </Typography>
            {endAdornment && <Box display="flex">{endAdornment}</Box>}
          </Stack>
          {subtext && (
            <Typography variant="body-xs" {...subtextTypographyProps}>
              {subtext}
            </Typography>
          )}
        </Stack>
        {(iconAfter || selected || loading) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              // A trailing component can render null, such as a suggestion badge.
              '&:empty': { display: 'none' },
            }}
          >
            {iconAfter && !loading && iconAfter}
            {!loading && selected && (
              <CheckIcon size={16} aria-label="Selected" />
            )}
            {loading && (
              <Loader
                boxProps={{
                  sx: {
                    display: 'flex',
                  },
                }}
                circularProgressProps={{
                  size: 14,
                  sx: {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            )}
          </Stack>
        )}
      </Stack>
    </MUIMenuItem>
  );
}
